/**
 * The application's URL table.
 *
 * Warning: No path is hard-coded anywhere else: renaming a route must never mean
 * grepping for strings. Parameterised routes are functions, so the types force
 * the parameter to be supplied.
 */
export const ROUTES = {
  /** The public site's home page — and, on a phone, the sign-in form. */
  home: '/',
  /** The public help centre: questions first, a human last. */
  support: '/support',
  /** What changed, and where the legal texts live. */
  about: '/about',

  login: '/login',
  signup: '/signup',
  /** Page d'atterrissage du retour OAuth — voir features/auth/googleSignIn.ts. */
  oauthSuccess: '/oauth-success',
  /** Confirmation du pseudo pour une inscription Google en attente. */
  welcome: '/welcome',

  /**
   * The three screens the transactional emails link to.
   *
   * ⚠️ These paths are quoted inside messages that have already been sent, and
   * a link in somebody's inbox cannot be redeployed. Renaming one of them
   * breaks every message still sitting unread — they are the only routes here
   * that are not free to move. See MailService.
   */
  /**
   * Where somebody says why they closed their account.
   *
   * ⚠️ No token, and that is the design rather than an omission: a token in
   * the URL is exactly what would let an answer be traced back to the account
   * that left. The page promises anonymity and the address is what keeps it.
   */
  farewell: '/adieu',

  verifyEmail: '/verify-email',
  /**
   * Where an account waits while its address is unconfirmed.
   *
   * ⚠️ Not linked from a message — `RequireAuth` sends people here. It is the
   * only screen an unconfirmed account can open, so it has to carry the way
   * out: another link, or the door.
   */
  verifyEmailPending: '/verify-email/pending',
  /** Behind « ce n'est pas moi ». ⚠️ Opens a page; deletes only on a click. */
  verifyEmailReject: '/verify-email/reject',
  forgotPassword: '/forgot-password',
  resetPassword: '/reset-password',

  /**
   * The legal documents, linked from the footer of every page of the site,
   * from the sign-up form and from the settings.
   *
   * Warning: four addresses, not one page with anchors. Each is quoted on its
   * own — a privacy policy is linked by a store, a legal notice by a host —
   * and an anchor is not a document.
   */
  terms: '/terms',
  privacy: '/privacy',
  gdpr: '/gdpr',
  legalNotice: '/mentions-legales',

  /**
   * The first launch: a welcome and three steps, shown once.
   *
   * Warning: an address of its own rather than a modal over the dashboard —
   * the sequence owns the screen, and somebody who reloads mid-way lands back
   * where they were rather than on a dashboard they have not been shown yet.
   */
  onboarding: '/onboarding',

  dashboard: '/dashboard',
  library: '/library',
  sessions: '/sessions',
  /**
   * The sessions screen with a circle already seated.
   *
   * Warning: a query parameter rather than router state, so the link survives
   * a reload and can be sent to somebody. The screen seats them once and drops
   * the parameter — coming back to a stale URL must not seat everybody twice.
   */
  sessionsWithCircle: (conversationId: string) =>
    `/sessions?circle=${encodeURIComponent(conversationId)}`,
  /**
   * Une partie en cours, en plein écran.
   *
   * ⚠️ Hors de `AppLayout` : pendant une partie l'écran ne sert qu'à ça, et la
   * navigation inviterait à en sortir par accident. On en sort par
   * « Suspendre » ou « Fin de partie », qui règlent la session d'abord.
   */
  livePlay: (sessionId: string) => `/sessions/${sessionId}/play`,
  store: '/store',
  community: '/community',
  wishlist: '/wishlist',
  family: '/family',
  /**
   * Page d'atterrissage d'un lien de partage de foyer.
   *
   * An address of its own rather than a parameter on `/family`: it describes
   * the household **before** joining it, and a received link can be opened by
   * someone signed out — `RequireAuth` then remembers the destination and
   * returns there after signing in.
   */
  familyJoin: (token: string) => `/family/join/${encodeURIComponent(token)}`,

  settings: '/settings',
  /**
   * The settings sections. Each has its own URL: on a phone it is a full page,
   * on a wide screen the panel on the right — the same address either way, so a
   * link stays shareable across devices.
   */
  settingsAppearance: '/settings/appearance',
  settingsProfile: '/settings/profile',
  settingsAccount: '/settings/account',
  settingsAbout: '/settings/about',
  notifications: '/notifications',
  messages: '/messages',
  /**
   * One conversation.
   *
   * Warning: a factory, so the id is encoded. A raw template would break on
   * anything the server ever puts in an id.
   */
  conversation: (id: string) => `/messages/${encodeURIComponent(id)}`,
  scan: '/scan',

  /**
   * Profil d'un joueur. Une seule URL, qu'on regarde le sien ou celui d'un
   * autre : les maquettes `meeply-profile-owner` et `meeply-profile-public`
   * both show `meeply.app/u/camille` — one page, rendered differently depending
   * on whether you own it. No separate `/me`, which would be a second
   * address for one content and would not be shareable.
   */
  profile: (username: string) => `/u/${username}`,

  game: (slug: string) => `/games/${slug}`,

  /**
   * Writing a sheet the catalogue does not have.
   *
   * Warning: a static segment under the same prefix as `game(:id)`. React
   * Router ranks static above dynamic, so /games/new is never read as a game
   * whose id is "new" — but the two must stay declared side by side, or the
   * next person will renumber one of them alone.
   */
  newGame: '/games/new',

  /**
   * Correcting a sheet somebody wrote.
   *
   * Warning: a segment **after** the id, so it can never be read as a game
   * whose id is "modifier". Only its creator is let in — the server checks,
   * and the button is only drawn for them.
   */
  editGame: (id: string) => `/games/${id}/modifier`,

  /**
   * Starts a session, possibly for a game already chosen — the play button on
   * the library cards.
   *
   * Warning: `/sessions` is still a `PagePlaceholder`: the parameter is set but
   * nobody reads it yet. It is written here rather than in the library so the
   * Sessions slice finds the contract already defined instead of inventing a
   * second one.
   */
  newSession: (gameId?: string) =>
    gameId ? `/sessions?game=${encodeURIComponent(gameId)}` : '/sessions',
} as const;
